import {
    FC,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react';

import useStorage from '@src/ui/hooks/useStorage';

import { useSentryLogger } from '../hooks/log';
import {
    CreditCommitmentsData,
    DebtsToConsolidate,
} from '../interfaces/debtcon';
import {
    addCreditCommitment,
    consolidateCreditCommitmentData,
    editCreditCommitment,
    getCreditCommitments,
    saveMainGoal,
} from '../requests/debtCon';
import { httpErrorResponse } from '../requests/shared';
import { parseDebtConDataFromRequest } from '../requests/transformFromRequest';
import { transformDebtToRequest } from '../requests/transformToRequest';

import { ApplicationContext } from './Application';

export const DebtConContext = createContext<any>(null);

interface DebtConContextProviderProps {
    children?: ReactNode;
    getAccessToken: Function;
    getIdToken: Function;
}

const DebtConContextProvider: FC<DebtConContextProviderProps> = (props) => {
    const { applicationId, applicants } = useContext(ApplicationContext);
    const [loading, setLoading] = useState(false);

    const [creditCommitmentsData, setCreditCommitmentsData] =
        useState<CreditCommitmentsData[]>();

    const [debtsToConsolidate, setDebtsToConsolidate] = useStorage<
        DebtsToConsolidate[] | null
    >('local', `debtsToConsolidate_${applicationId}`);

    const { handleError } = useSentryLogger(applicationId, 'DebtCon context');

    const { getAccessToken, getIdToken } = props;

    const [currentDebtIndex, setCurrentDebtIndex] = useState(0);

    const [reviewPageNav, setReviewPageNav] = useState(false);

    const currentDebt = creditCommitmentsData?.[currentDebtIndex];

    const [toggleStates, setToggleStates] = useStorage<Record<
        string,
        boolean
    > | null>('local', `toggleStates_${applicationId}`, {});

    const fetchCreditCommitments = useCallback(
        async (option?: boolean) => {
            try {
                setLoading(true);
                const result = await getCreditCommitments(
                    applicationId,
                    await getAccessToken(),
                    await getIdToken(),
                    option,
                );

                if ('data' in result) {
                    // removes duplicates as a user defined joint CC will be returned twice
                    const data = Array.from(
                        new Map(
                            result.data.map((item: any) => [item.id, item]),
                        ).values(),
                    );

                    const transformedData = parseDebtConDataFromRequest(
                        data,
                        applicants,
                    );

                    const initialDebtIndex = transformedData.findIndex(
                        (debt) =>
                            !debt.lenderName &&
                            !debt.dffDebtConSkipped &&
                            !debt.ignore,
                    );

                    setCurrentDebtIndex(
                        initialDebtIndex !== -1
                            ? initialDebtIndex
                            : transformedData.length - 1,
                    );

                    setCreditCommitmentsData(transformedData);
                }
                return result;
            } catch (e) {
                /* dont want this captured in sentry as it will capture errors for users whoes credit file we havent yet tried to pull, 
                 or those who we have tried to pull but were unable to get. For these users we will skip debt con and run decisioning */
                return httpErrorResponse(e);
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [applicationId],
    );

    const consolidateCreditCommitments = async () => {
        try {
            setLoading(true);
            const response = await consolidateCreditCommitmentData(
                applicationId,
                await props.getAccessToken(),
                await props.getIdToken(),
                debtsToConsolidate,
            );
            return response;
        } catch (e) {
            return handleError(e, 'consolidating credit commitments');
        } finally {
            setLoading(false);
        }
    };

    const updateCreditCommitment = async (data: any) => {
        try {
            setLoading(true);
            const response = await editCreditCommitment(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
                transformDebtToRequest(data, applicants),
            );
            return response;
        } catch (e) {
            return handleError(e, 'updating credit commitments');
        } finally {
            setLoading(false);
        }
    };

    const createCreditCommitment = async (data: any) => {
        try {
            setLoading(true);
            const response = await addCreditCommitment(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
                transformDebtToRequest(data, applicants),
            );
            return response;
        } catch (e) {
            return handleError(e, 'updating credit commitments');
        } finally {
            setLoading(false);
        }
    };

    const selectMainGoal = async (data: string) => {
        try {
            setLoading(true);
            const response = await saveMainGoal(
                applicationId,
                data,
                await getAccessToken(),
                await getIdToken(),
            );
            return response;
        } catch (e) {
            return handleError(e, 'selecting main goal');
        } finally {
            setLoading(false);
        }
    };

    return (
        <DebtConContext.Provider
            value={{
                loading,
                creditCommitmentsData,
                setCreditCommitmentsData,
                fetchCreditCommitments,
                currentDebt,
                setCurrentDebtIndex,
                currentDebtIndex,
                debtsToConsolidate,
                setDebtsToConsolidate,
                consolidateCreditCommitments,
                updateCreditCommitment,
                createCreditCommitment,
                toggleStates,
                setToggleStates,
                reviewPageNav,
                setReviewPageNav,
                selectMainGoal,
            }}
        >
            {props.children}
        </DebtConContext.Provider>
    );
};

export default DebtConContextProvider;
